import {PrintStoryDoc, PrintStoryEvent} from '../types'
import {toMillis, getLatestMillis} from '../time'

export const mapContentLifecycleToEvents = ({
  content,
  codes = [],
  renderJobs = [],
}: {
  content?: PrintStoryDoc | null
  codes?: PrintStoryDoc[]
  renderJobs?: PrintStoryDoc[]
}): PrintStoryEvent[] => {
  const events: PrintStoryEvent[] = []
  const contentId = content?.id ? String(content.id) : undefined

  const createdAt =
    toMillis(content?.dateCreated) ||
    toMillis(content?.createdAt) ||
    toMillis(content?.createdOn) ||
    toMillis(content?.createdAtMs)

  if (createdAt && content) {
    events.push({
      ts: createdAt,
      lane: 'content',
      kind: 'content_created',
      severity: 'info',
      contentId,
      title: 'Content created',
      detail: content.api ? `API: ${content.api}` : undefined,
      sourceCollection: '_contents',
      raw: content,
    })
  }

  for (const code of codes) {
    const codeTs =
      toMillis(code.createdAt) ||
      toMillis(code.dateCreated) ||
      toMillis(code.createdAtMs) ||
      toMillis(code.updatedAt)

    if (!codeTs) {
      continue
    }

    events.push({
      ts: codeTs,
      lane: 'code',
      laneKey: String(code.codeId || code.id),
      kind: 'code_assigned',
      severity: 'info',
      contentId,
      codeId: String(code.codeId || code.id),
      deviceId: code.deviceId ? String(code.deviceId) : undefined,
      title: 'Code assigned',
      detail: code.code || code.codeId || code.id,
      sourceCollection: 'codes',
      raw: code,
    })
  }

  for (const job of renderJobs) {
    const started =
      toMillis(job.timestamps?.createdAt) ||
      toMillis(job.createdAtMs) ||
      toMillis(job.createdAt) ||
      toMillis(job.startedAt)

    const status = String(job.status || '').toLowerCase()
    const completed =
      toMillis(job.timestamps?.completedAt) ||
      toMillis(job.completedAt) ||
      toMillis(job.updatedAtMs) ||
      toMillis(job.updatedAt)

    if (started) {
      events.push({
        ts: started,
        lane: 'render',
        laneKey: String(job.jobId || job.id),
        kind: 'render_started',
        severity: 'info',
        contentId,
        codeId: job.codeId ? String(job.codeId) : undefined,
        jobId: String(job.jobId || job.id),
        title: 'Composite / render started',
        detail: job.reason || job.staffMessage,
        sourceCollection: 'renderJobs',
        raw: job,
      })
    }

    if (completed && (status === 'completed' || status === 'failed')) {
      events.push({
        ts: completed,
        lane: 'render',
        laneKey: String(job.jobId || job.id),
        kind: status === 'failed' ? 'render_failed' : 'render_completed',
        severity: status === 'failed' ? 'danger' : 'success',
        contentId,
        codeId: job.codeId ? String(job.codeId) : undefined,
        jobId: String(job.jobId || job.id),
        title: status === 'failed' ? 'Composite / render failed' : 'Composite / render completed',
        detail: job.lastError || job.error || job.staffMessage,
        sourceCollection: 'renderJobs',
        raw: job,
      })
    }
  }

  if (content) {
    const emails = Array.isArray(content.emails) ? content.emails : []
    const mailTs =
      toMillis(content.mailSentAt) ||
      toMillis(content.emailSentAt) ||
      toMillis(content.lastMailAt) ||
      toMillis(content.mailsSentAt) ||
      getLatestMillis(
        (Array.isArray(content.mailHistory) ? content.mailHistory : []).map(
          (item: any) => item?.sentAt || item?.createdAt || item?.timestamp
        )
      )

    if (emails.length > 0 || content.mails || mailTs) {
      events.push({
        ts: mailTs || createdAt || Date.now(),
        lane: 'mail',
        kind: 'mail_sent',
        severity: emails.length > 0 || content.mails ? 'success' : 'neutral',
        contentId,
        title: 'Mail sent',
        detail: mailTs
          ? `${emails.length || content.mails || 0} recipient(s)`
          : `Mail present (${emails.length || content.mails || 0}) — exact send time unknown`,
        sourceCollection: '_contents',
        raw: {emails, mails: content.mails, mailTs},
      })
    }
  }

  return events
}
